class WhyAlSultan extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.groups = [];
    this.items = [];
    this.isDesktop = false;
    this.hasAnimated = false;
    this.onResize = this.onResize.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemKeydown = this.onItemKeydown.bind(this);
    this.onItemPointerEnter = this.onItemPointerEnter.bind(this);
    this.onItemPointerLeave = this.onItemPointerLeave.bind(this);
    this.onItemPointerDown = this.onItemPointerDown.bind(this);
  }

  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('.why-al-sultan__group'));
    this.items = Array.from(this.querySelectorAll('.why-al-sultan__item'));
    if (!this.groups.length) return;

    this.activeIndex = Math.max(0, parseInt(this.dataset.defaultActive, 10) || 0);
    if (this.activeIndex >= this.groups.length) this.activeIndex = 0;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
    this.isDesktop = this.mediaQuery.matches;

    this.bindEvents();
    this.setActive(this.activeIndex, { immediate: true });
    this.initObserver();

    this.mediaQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
    this.items.forEach((item) => {
      item.removeEventListener('click', this.onItemClick);
      item.removeEventListener('keydown', this.onItemKeydown);
      item.removeEventListener('pointerenter', this.onItemPointerEnter);
      item.removeEventListener('pointerleave', this.onItemPointerLeave);
      item.removeEventListener('pointerdown', this.onItemPointerDown);
    });
  }

  bindEvents() {
    this.items.forEach((item, index) => {
      item.addEventListener('click', this.onItemClick);
      item.addEventListener('keydown', this.onItemKeydown);
      item.addEventListener('pointerenter', this.onItemPointerEnter);
      item.addEventListener('pointerleave', this.onItemPointerLeave);
      item.addEventListener('pointerdown', this.onItemPointerDown);
      item.dataset.index = String(index);
    });
  }

  initObserver() {
    if (this.reducedMotion) {
      this.classList.add('is-visible');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.classList.add('is-visible');
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    this.observer.observe(this);
  }

  onResize() {
    this.isDesktop = this.mediaQuery.matches;
  }

  onItemClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(index)) {
      this.setActive(index);
    }
  }

  onItemKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(index)) {
      this.setActive(index);
    }
  }

  onItemPointerEnter(event) {
    if (!this.isDesktop) return;
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(index)) {
      this.setActive(index);
    }
  }

  onItemPointerLeave(event) {
    if (!this.isDesktop) return;
    /* Keep last hovered active — no reset needed */
  }

  onItemPointerDown(event) {
    const item = event.currentTarget;
    item.classList.add('is-tapping');
    window.setTimeout(() => item.classList.remove('is-tapping'), 140);
  }

  setActive(index, { immediate = false } = {}) {
    if (index === this.activeIndex && !immediate) return;

    const previousGroup = this.groups[this.activeIndex];
    const nextGroup = this.groups[index];

    this.groups.forEach((group, i) => {
      const item = this.items[i];
      const isActive = i === index;

      group.classList.toggle('is-active', isActive);
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-expanded', isActive ? 'true' : 'false');

      const desc = item.querySelector('.why-al-sultan__desc');
      if (desc) desc.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.activeIndex = index;
  }
}

if (!customElements.get('why-al-sultan')) {
  customElements.define('why-al-sultan', WhyAlSultan);
}
